<template>
  <oie-my-pages-template
    v-if="myMachines"
    class="my-machines"
    v-bind="inertAttribute"
    :title="t('myMachinesTitle')"
    :text="t('myMachinesSubTitle')">
    <oie-action-wrapper>
      <oie-base-action
        v-tooltip.top="tStatic('newMachineOwnerTooltip')"
        theme="flat"
        :only-icon-mobile="true"
        @click="handleCreateNewMachineOwner">
        <template #iconPrepend>
          <oie-base-icon icon="user-follow" size="s"></oie-base-icon>
        </template>
        {{ tStatic('newMachineOwner') }}
      </oie-base-action>
      <oie-base-action
        v-tooltip.top="tStatic('newMachineTooltip')"
        theme="flat"
        :only-icon-mobile="true"
        @click="goToSubPage(navigationOptions.oieAddOrEditMachine)">
        <template #iconPrepend>
          <oie-base-icon icon="machine" size="s"></oie-base-icon>
        </template>
        {{ tStatic('newMachine') }}
      </oie-base-action>
      <oie-base-action
        v-tooltip.top="t('machineSettingsTooltip')"
        theme="flat"
        :only-icon-mobile="true"
        @click="goToSubPage(navigationOptions.oieMyMachineSettings)">
        <template #iconPrepend>
          <oie-base-icon icon="settings" size="s"></oie-base-icon>
        </template>
        {{ t('machineSettings') }}
      </oie-base-action>
    </oie-action-wrapper>
    <oie-action-wrapper>
      <template #left>
        <oie-base-input v-model="filter" :placeholder="tStatic('filterMachines')">
          <template #iconPrepend>
            <oie-base-icon icon="filter-icon" size="s"></oie-base-icon>
          </template>
        </oie-base-input>
      </template>
      <template #right>
        <oie-base-dropdown v-model="sortOrder" :initial-text="tStatic('selectSort')" :options="[{value: 'asc', text: tStatic('sortOrderAsc')}, {value: 'desc', text: tStatic('sortOrderDesc')}]"></oie-base-dropdown>
      </template>
    </oie-action-wrapper>
    <oie-my-machines-list-wrapper v-if="sortedMachineOwners.length > 0">
      <template #title>
        <h2>
          {{ tStatic('machineOwnersTitle') }}
        </h2>
      </template>
      <li v-for="customer in sortedMachineOwners" :key="`customer-${customer.machineOwnerId}`">
        <oie-machine-owner-item :customer="customer" @select-machine-owner="handleSelectMachineOwner" @select-machine="handleSelectMachine">
          <template #machine-actions="{ machine }">
            <oie-base-action
              v-if="machine.sparePartsUrl"
              theme="outlined"
              :url="`${machine.sparePartsUrl}?hasComeFromMachine=${machine.machineId}`">
              <template #iconPrepend>
                <oie-base-icon
                  icon="machine-spare-parts"
                  size="s"></oie-base-icon>
              </template>
              {{ tStatic('spareParts') }}
            </oie-base-action>
          </template>
        </oie-machine-owner-item>
      </li>
    </oie-my-machines-list-wrapper>
    <oie-my-machines-list-wrapper v-if="sortedMachines.length > 0">
      <template #title>
        <h2>
          {{ tStatic('machinesTitle') }}
        </h2>
      </template>
      <li v-for="machine in sortedMachines" :key="`machine-${machine.machineId}`">
        <oie-machine-item :machine="machine" @select-machine="handleSelectMachine">
          <template #actions>
            <oie-base-action
              v-if="machine.sparePartsUrl"
              theme="outlined"
              :url="`${machine.sparePartsUrl}?hasComeFromMachine=${machine.machineId}`">
              <template #iconPrepend>
                <oie-base-icon
                  icon="machine-spare-parts"
                  size="s"></oie-base-icon>
              </template>
              {{ tStatic('spareParts') }}
            </oie-base-action>
          </template>
        </oie-machine-item>
      </li>
    </oie-my-machines-list-wrapper>
    <p v-if="sortedMachineOwners.length === 0 && sortedMachines.length === 0">
      {{ tStatic('noMachinesOrCustomers') }}
    </p>
  </oie-my-pages-template>
</template>

<script>
import myPages from '@/mixins/my-pages'
import { mapGetters, mapActions } from 'vuex'
import _ from 'lodash'
import { showMyMachinesGdprConsentModal } from '@/core/modal-service'

export default {
  mixins: [myPages],
  data() {
    return {
      sortOrder: 'asc'
    }
  },
  computed: {
    ...mapGetters('myMachines', ['myMachines', 'myMachinesFilter', 'filteredMachineOwners', 'filteredMachines', 'myMachinesIsLocked']),
    sortedMachineOwners() {
      return _.orderBy(this.filteredMachineOwners, (owner) => owner.name ? owner.name.toLowerCase() : owner.name, [this.sortOrder]) || []
    },
    sortedMachines() {
      return _.orderBy(this.filteredMachines, (machine) => machine.name ? machine.name.toLowerCase() : machine.name, [this.sortOrder]) || []
    },
    filter: {
      get() { return this.myMachinesFilter },
      set(val) {
        this.setMyMachinesFilter({ filter: val })
      }
    },
    inertAttribute() {
      return this.myMachinesIsLocked ? { inert: true } : undefined
    }
  },
  async mounted() {
    this.setCurrentMachine(null)
    this.setCurrentMachineOwner(null)
    this.getMyMachinesMetaData()
    this.getVehicles()
    await this.getMyMachines()
    if (this.myMachinesIsLocked) {
      showMyMachinesGdprConsentModal()
    }
  },
  methods: {
    ...mapActions('myMachines', ['getMyMachines', 'getMyMachinesMetaData', 'getVehicles', 'setCurrentMachine', 'setCurrentMachineOwner', 'setMyMachinesFilter']),
    handleSelectMachineOwner(machineOwner) {
      this.setCurrentMachineOwner(machineOwner)
      this.goToSubPage(this.navigationOptions.oieMachineOwnerDetails)
    },
    handleSelectMachine(machine) {
      this.setCurrentMachine(machine)
      this.goToSubPage(this.navigationOptions.oieMachineDetails)
    },
    async handleCreateNewMachineOwner() {
      if (this.myMachines.isGdprConsentGiven) {
        this.goToSubPage(this.navigationOptions.oieAddOrEditMachineOwner)
      } else {
        const consent = await showMyMachinesGdprConsentModal()
        if(consent) {
          this.goToSubPage(this.navigationOptions.oieAddOrEditMachineOwner)
        }
      }
    }
  }
}

</script>

<style lang="scss">
.my-machines[inert] {
  filter: blur(5px);
}
</style>
